/**
 * SCEditor Email Converter
 * 
 * Converts CSP-compliant CSS classes back to inline styles for email compatibility.
 * Email clients don't support external CSS or <style> tags, so inline styles are required.
 */

/**
 * Converts SCEditor CSP classes back to inline styles for email compatibility
 * @param {string} html - HTML content with CSP-compliant classes
 * @returns {string} - HTML content with inline styles for email
 */
export function convertClassesToInlineStyles(html) {
  if (!html || typeof html !== 'string') return html;
  
  // Create a temporary container to parse HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;
  
  // Find all elements with SCEditor classes
  const elementsWithClasses = temp.querySelectorAll('[class*="sce-"]');
  
  elementsWithClasses.forEach(element => {
    const classes = element.className ? element.className.split(' ') : [];
    const styles = [];
    
    // Process each class
    classes.forEach(className => {
      if (className.startsWith('sce-')) {
        // Text decoration
        if (className === 'sce-underline') {
          styles.push('text-decoration: underline');
        } else if (className === 'sce-strike') {
          styles.push('text-decoration: line-through');
        }
        
        // Text alignment
        else if (className === 'sce-align-left') {
          styles.push('text-align: left');
        } else if (className === 'sce-align-center') {
          styles.push('text-align: center');
        } else if (className === 'sce-align-right') {
          styles.push('text-align: right');
        } else if (className === 'sce-align-justify') {
          styles.push('text-align: justify');
        }
        
        // Font family
        else if (className === 'sce-font-arial') {
          styles.push('font-family: Arial, sans-serif');
        } else if (className === 'sce-font-arial-black') {
          styles.push('font-family: "Arial Black", sans-serif');
        } else if (className === 'sce-font-comic-sans-ms') {
          styles.push('font-family: "Comic Sans MS", cursive');
        } else if (className === 'sce-font-courier-new') {
          styles.push('font-family: "Courier New", monospace');
        } else if (className === 'sce-font-georgia') {
          styles.push('font-family: Georgia, serif');
        } else if (className === 'sce-font-impact') {
          styles.push('font-family: Impact, sans-serif');
        } else if (className === 'sce-font-sans-serif') {
          styles.push('font-family: sans-serif');
        } else if (className === 'sce-font-serif') {
          styles.push('font-family: serif');
        } else if (className === 'sce-font-times-new-roman') {
          styles.push('font-family: "Times New Roman", serif');
        } else if (className === 'sce-font-trebuchet-ms') {
          styles.push('font-family: "Trebuchet MS", sans-serif');
        } else if (className === 'sce-font-verdana') {
          styles.push('font-family: Verdana, sans-serif');
        }
        
        // Font size
        else if (className.startsWith('sce-size-')) {
          const size = className.replace('sce-size-', '');
          styles.push(`font-size: ${size}`);
        }
        
        // Color (from CSS custom property or data attribute)
        else if (className === 'sce-color') {
          const colorValue = element.getAttribute('data-sce-color') || 
                           element.style.getPropertyValue('--sce-color-value') ||
                           getComputedStyle(element).getPropertyValue('--sce-color-value');
          if (colorValue) {
            styles.push(`color: ${colorValue.trim()}`);
          }
        }
        
        // Font family (from CSS custom property or data attribute)
        else if (className === 'sce-font-family') {
          const fontValue = element.getAttribute('data-sce-font-family') || 
                          element.style.getPropertyValue('--sce-font-family-value') ||
                          getComputedStyle(element).getPropertyValue('--sce-font-family-value');
          if (fontValue) {
            styles.push(`font-family: ${fontValue.trim()}`);
          }
        }
        
        // Font size (from CSS custom property or data attribute)
        else if (className === 'sce-font-size') {
          const sizeValue = element.getAttribute('data-sce-font-size') || 
                          element.style.getPropertyValue('--sce-font-size-value') ||
                          getComputedStyle(element).getPropertyValue('--sce-font-size-value');
          if (sizeValue) {
            styles.push(`font-size: ${sizeValue.trim()}`);
          }
        }
        
        // Direction
        else if (className === 'sce-direction-rtl') {
          styles.push('direction: rtl');
        } else if (className === 'sce-direction-ltr') {
          styles.push('direction: ltr');
        }
      }
    });
    
    // Apply inline styles if we have any
    if (styles.length > 0) {
      const existingStyle = element.getAttribute('style') || '';
      const existingStyles = existingStyle.split(';').filter(s => s.trim());
      const allStyles = [...existingStyles, ...styles].filter(s => s.trim());
      element.setAttribute('style', allStyles.join('; '));
    }
    
    // Remove SCEditor classes (keep other classes)
    const remainingClasses = classes.filter(c => !c.startsWith('sce-'));
    if (remainingClasses.length > 0) {
      element.className = remainingClasses.join(' ');
    } else {
      element.removeAttribute('class');
    }
    
    // Remove data attributes used for CSS custom properties
    element.removeAttribute('data-sce-color');
    element.removeAttribute('data-sce-font-family');
    element.removeAttribute('data-sce-font-size');
  });
  
  return temp.innerHTML;
}
